import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import { getAppliedJobDetails } from "../helper/My_Helper";

const Form12B = ({ candidateData = {} }) => {
  const formRef = useRef();
  const appliedJob = getAppliedJobDetails(candidateData);
  const placeofPosting = candidateData?.annexure_eleven_form_data?.place_of_posting || appliedJob?.proposed_location || appliedJob?.job_location || candidateData?.location || '';

  // Get table data from response
  const mainTableData = appliedJob?.form12B_mainTable || [];
  const annexureTable1Data = appliedJob?.form12B_annexureTable1 || [];
  const annexureTable2Data = appliedJob?.form12B_annexureTable2 || [];

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 8mm 10mm;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        .page-break {
          page-break-before: always;
        }
        .page-break-avoid {
          page-break-inside: avoid;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  // Helper function to get candidate name
  const getCandidateName = () => {
    const fullName = candidateData?.applicant_form_data?.full_name;
    if (typeof fullName === "object") {
      return [fullName?.first_name, fullName?.middle_name, fullName?.surname]
        .filter(Boolean)
        .join(" ");
    }
    return candidateData?.name || "";
  };

  // Helper function to get PAN number
  const getPanNumber = () => {
    return candidateData?.annexure_eleven_form_data?.pan_number || "";
  };

  // Helper function to get communication address
  const getCommunicationAddress = () => {
    const address = candidateData?.applicant_form_data?.communication_address;
    if (address) {
      return `${address.address || ""}, ${address.pincode || ""}`;
    }
    return "";
  };

  // Helper function to get current year
  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  // Helper function to get current date
  const getCurrentDate = () => {
    const date = new Date();
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
  };

  // Helper function to get employment details
  const getEmploymentDetails = () => {
    const employment = candidateData?.applicant_form_data?.employment_history?.[0];
    if (employment) {
      return {
        orgName: employment.org_name || "",
        designation: employment.designation || "",
        fromDate: employment.duration_from ? new Date(employment.duration_from).toLocaleDateString() : "",
        toDate: employment.duration_to ? new Date(employment.duration_to).toLocaleDateString() : "Present",
      };
    }
    return { orgName: "", designation: "", fromDate: "", toDate: "" };
  };

  // Helper function to get salary details
  const getSalaryDetails = () => {
    const paySlip = candidateData?.applicant_form_data?.pay_slip || {};
    // Calculate total salary (basic + all allowances)
    const basic = parseFloat(paySlip.basic) || 0;
    const allowances = Object.entries(paySlip)
      .filter(([key]) => key !== 'basic' && key !== 'provident_fund' && key !== 'superannuation' && key !== 'gratuity')
      .reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0);
    
    return {
      basic,
      allowances,
      totalSalary: basic + allowances,
    };
  };

  const cellStyle = {
    border: "1px solid #000",
    fontSize: "9.5px",
    p: 0.5,
    verticalAlign: "top",
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#f5f5f5",
  };

  const employment = getEmploymentDetails();
  const salary = getSalaryDetails();

  return (
    <Box>
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          fontFamily: '"Times New Roman", Times, serif',
          maxWidth: "297mm",
          mx: "auto",
          color: "#000",
        }}
      >
        {/* ===================== PAGE 1 ===================== */}
        <Box sx={{ p: 1, pageBreakAfter: "always", pageBreakInside: "avoid" }}>
          {/* Header */}
          <Box sx={{ position: "relative", mb: 1, minHeight: "36px" }}>
            <Box sx={{ position: "absolute", left: 0, top: 0 }}>
              <img
                src={
                  config?.IMAGE_PATH
                    ? config.IMAGE_PATH + "logo512.png"
                    : "/logo512.png"
                }
                alt="Logo"
                height="36"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo512.png";
                }}
              />
            </Box>
            <Typography
              align="center"
              sx={{ fontSize: "14px", fontWeight: "bold", pt: 0.2 }}
            >
              FORM NO. 12B
            </Typography>
            <Typography align="center" sx={{ fontSize: "10.5px" }}>
              [See rule 26A]
            </Typography>
            <Typography
              align="center"
              sx={{ fontSize: "11.5px", fontWeight: "bold", mt: 0.5 }}
            >
              Form for furnishing details of income under section 192(2) for the
              year ending 31st March, {getCurrentYear()}
            </Typography>
          </Box>

          {/* Employee Details */}
          <Grid container spacing={0.8} sx={{ mb: 1 }}>
            <Grid item xs={8}>
              <Typography sx={{ fontSize: "10.5px" }}>
                Name and address of the employee
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "20px",
                  px: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "10.5px" }}>
                  {getCandidateName()}
                  <br />
                  {getCommunicationAddress()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "10.5px" }}>
                Permanent Account No.
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "20px",
                }}
              >
                <Typography sx={{ fontSize: "10.5px", ml: 1 }}>
                  {getPanNumber()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: "10.5px", whiteSpace: "nowrap" }}>
                  Residential status
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    borderBottom: "1px solid #000",
                    minHeight: "20px",
                  }}
                >
                  <Typography sx={{ fontSize: "10.5px", ml: 1 }}>
                    Resident
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Typography
            sx={{ fontSize: "10.5px", fontWeight: "bold", mb: 0.8 }}
          >
            Particulars of salary as defined in section 17, paid or due to be
            paid to the employee during the year
          </Typography>

          {/* Main Table - Page 1 - Dynamically from form12B_mainTable */}
          <TableContainer sx={{ border: "1px solid #000", mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "4%", fontSize: "8.5px", p: 0.3 }}>
                    S. No
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%", fontSize: "8.5px", p: 0.3 }}>
                    Name and address of employer(s)
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "8%", fontSize: "8.5px", p: 0.3 }}>
                    TAN of the employer(s) as allotted by the ITO
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "8%", fontSize: "8.5px", p: 0.3 }}>
                    Permanent Account Number of the employer(s)
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "7%", fontSize: "8.5px", p: 0.3 }}>
                    Period of employment
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "9%", fontSize: "8.5px", p: 0.3 }}>
                    Total amount of salary excluding amounts required to be shown in columns 7 and 8
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%", fontSize: "8.5px", p: 0.3 }}>
                    Total amount of house rent allowance, conveyance allowance and other allowances to the extent chargeable to tax [See section 10(13A) read with rule 2A and section 10(14)]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "9%", fontSize: "8.5px", p: 0.3 }}>
                    Value of perquisites and amount of accretion to employee's provident fund account (give details in the Annexure)
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "7%", fontSize: "8.5px", p: 0.3 }}>
                    Total of columns 6, 7 and 8
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "9%", fontSize: "8.5px", p: 0.3 }}>
                    Amount deducted in respect of life insurance premium, provident fund contribution, etc., to which sec. 80C applies (Give details)
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "8%", fontSize: "8.5px", p: 0.3 }}>
                    Total amount of tax deducted during the year (enclose certificate issued under section 203)
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "6%", fontSize: "8.5px", p: 0.3 }}>
                    Remarks
                  </TableCell>
                </TableRow>
                <TableRow>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <TableCell
                      key={n}
                      sx={{
                        ...headerCellStyle,
                        fontSize: "8.5px",
                        p: 0.25,
                      }}
                    >
                      {n}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {mainTableData.length > 0 ? (
                  mainTableData.map((row, index) => (
                    <TableRow key={row._id || index}>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.employerName || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.tan || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.employerPan || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.periodOfEmployment || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.totalSalary ? `₹${row.totalSalary}` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.allowances ? `₹${row.allowances}` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.perquisites ? `₹${row.perquisites}` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.totalOfCol6To8 ? `₹${row.totalOfCol6To8}` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.deductions || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.taxDeducted ? `₹${row.taxDeducted}` : ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                        {row.remarks || ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Fallback to default row if no data
                  <TableRow>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>1</TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                      {employment.orgName}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}></TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}></TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                      {employment.fromDate} - {employment.toDate}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                      ₹{salary.basic.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                      ₹{salary.allowances.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}></TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>
                      ₹{salary.totalSalary.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}></TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}></TableCell>
                    <TableCell sx={{ ...cellStyle, p: 0.25 }}>Current</TableCell>
                  </TableRow>
                )}
                {/* Add empty rows if needed to fill the table */}
                {mainTableData.length < 4 && (
                  [...Array(4 - mainTableData.length)].map((_, rowIndex) => (
                    <TableRow key={`empty-${rowIndex}`}>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{ ...cellStyle, height: "22px", p: 0.25 }}
                        />
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography sx={{ fontSize: "9px", mb: 0.5 }}>
            † With effect from 1-4-1991, section 80C has been replaced by section
            88.
          </Typography>

          <Typography align="right" sx={{ fontSize: "10.5px", mb: 0.5 }}>
            Signature of the employee
          </Typography>
          <Box
            sx={{
              borderBottom: "1px solid #000",
              width: "160px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: "auto",
              mb: 1.2,
            }}
          >
            {candidateData?.applicant_form_data?.signature && (
              <img
                src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                alt="candidate-signature"
                style={{
                  maxHeight: "22px",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>

          {/* Verification */}
          <Typography
            sx={{ fontSize: "11px", fontWeight: "bold", mb: 0.4 }}
          >
            Verification
          </Typography>
          <Typography sx={{ fontSize: "9.5px", mb: 0.8 }}>
            I, {getCandidateName()} do hereby
            declare that what is stated above is true to the best of my
            knowledge and belief. Verified today, the {getCurrentDate()}
          </Typography>
          <Grid container sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: "10.5px", whiteSpace: "nowrap" }}>
                  Place
                </Typography>
                <Box
                  sx={{
                    borderBottom: "1px solid #000",
                    width: "160px",
                    height: "20px",
                  }}
                >{placeofPosting}</Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: "10.5px", whiteSpace: "nowrap" }}>
                  Signature of the employee
                </Typography>
                <Box
                  sx={{
                    borderBottom: "1px solid #000",
                    width: "160px",
                    height: "24px",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  {candidateData?.applicant_form_data?.signature && (
                    <img
                      src={`${config?.IMAGE_PATH || ""}${
                        candidateData.applicant_form_data.signature
                      }`}
                      alt="candidate-signature"
                      style={{
                        maxHeight: 24,
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ===================== PAGE 2 ===================== */}
        <Box sx={{ p: 1.5, pageBreakAfter: "always" }}>
          <Typography
            align="center"
            sx={{ fontSize: "12px", fontWeight: "bold", mt: 0 }}
          >
            ANNEXURE
          </Typography>
          <Typography
            align="center"
            sx={{ fontSize: "12px", fontWeight: "bold", mb: 0.5 }}
          >
            [See column 8 of Form No. 12B]
          </Typography>
          <Typography
            align="center"
            sx={{ fontSize: "12px", fontWeight: "bold", mb: 1.5 }}
          >
            Particulars of value of perquisites and amount of accretion to
            employee's provident fund account
          </Typography>

          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={7}>
              <Typography sx={{ fontSize: "11px", mb: 0.5 }}>
                Name and address of the employee
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "20px",
                  px: 0.5,
                }}
              >
                <Typography sx={{ fontSize: "11px" }}>
                  {getCandidateName()}
                  <br />
                  {getCommunicationAddress()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={5}>
              <Typography sx={{ fontSize: "11px" }}>
                Permanent Account No.
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  minHeight: "20px",
                }}
              >
                <Typography sx={{ fontSize: "11px", ml: 1 }}>
                  {getPanNumber()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "11px" }}>
                Period : Year ending 31st March, {getCurrentYear()}
              </Typography>
            </Grid>
          </Grid>

          {/* Perquisites Table Part 1 - Dynamically from form12B_annexureTable1 */}
          <TableContainer sx={{ border: "1px solid #000", mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "8%" }} rowSpan={2}>
                    Name of the employee
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "8%" }} rowSpan={2}>
                    TAN/PAN of the employer
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellStyle }}
                    colSpan={7}
                  >
                    Value of rent-free accommodation or value of any concession
                    in rent for the accommodation provided by the employer
                    (give basis of computation) [See rules 3(a) and 3(b)]
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "9%" }}>
                    Where accommodation is unfurnished
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "9%" }}>
                    Value as if accommodation is unfurnished
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Cost of furniture (including television sets, radio sets, refrigerators, other household appliances and air-conditioning plant or equipment) OR hire charges, if hired from a third party
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Perquisite value of furniture (10% of column 5) OR actual hire charges payable
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "8%" }}>
                    Total of columns 4 and 6
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "8%" }}>
                    Rent, if any, paid by the employee
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Value of perquisite (column 3 minus column 8 or column 7 minus column 8 as may be applicable)
                  </TableCell>
                </TableRow>
                <TableRow>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <TableCell
                      key={n}
                      sx={{ ...headerCellStyle, fontSize: "9px", p: 0.3 }}
                    >
                      {n}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {annexureTable1Data.length > 0 ? (
                  annexureTable1Data.map((row, index) => (
                    <TableRow key={row._id || index}>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.employeeName || getCandidateName()}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.employerTanPan || getPanNumber()}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.unfurnishedValue || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.accommodationValue || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.furnitureCost || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.furniturePerquisite || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.totalCol4And6 || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.rentPaid || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.perquisiteValue || ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}>
                      {getCandidateName()}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}>
                      {getPanNumber()}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                  </TableRow>
                )}
                {/* Add empty rows if needed */}
                {annexureTable1Data.length < 4 && (
                  [...Array(4 - annexureTable1Data.length)].map((_, rowIndex) => (
                    <TableRow key={`empty-annex1-${rowIndex}`}>
                      {Array.from({ length: 9 }).map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{ ...cellStyle, height: "32px" }}
                        />
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ===================== PAGE 3 ===================== */}
        <Box sx={{ p: 1.5 }}>
          <Typography
            align="center"
            sx={{ fontSize: "12px", fontWeight: "bold", mb: 1.5, mt: 0 }}
          >
            ANNEXURE (Contd.)
          </Typography>

          {/* Annexure Table 2 - Dynamically from form12B_annexureTable2 */}
          <TableContainer sx={{ border: "1px solid #000", mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellStyle, width: "8%" }}>
                    Name of the employee
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "14%" }}>
                    Whether any conveyance has been provided by the employer free or at a concessional rate or where the employee is allowed the use of one or more motor cars owned or hired by the employer, estimated value of perquisite (give details) [See rule 3(c)]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Remuneration paid by employer for domestic and/or personal services provided to the employee (give details) [See rule 3(g)]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Value of free or concessional passages on home leave and other travelling to the extent chargeable to tax (give details) [See rule 2B read with section 10(5)(ii)]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "14%" }}>
                    Estimated value of any other benefit or amenity provided by the employer, free of cost or at concessional rate not included in the preceding columns (give details), e.g., supply of gas, electricity or water for household consumption, free educational facilities, transport for family, etc. [See rules 3(d), 3(e) and 3(f)]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Employer's contribution to recognised provident fund in excess of 10% of the employee's salary [See Schedule IV - Part A]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "12%" }}>
                    Interest credited to the assessee's account in recognised provident fund in excess of the rate fixed by the Central Government [See Schedule IV - Part A]
                  </TableCell>
                  <TableCell sx={{ ...headerCellStyle, width: "10%" }}>
                    Total of columns 9 to 15 carried to column 8 of Form No. 12B
                  </TableCell>
                </TableRow>
                <TableRow>
                  {[1, 10, 11, 12, 13, 14, 15, 16].map((n) => (
                    <TableCell
                      key={n}
                      sx={{ ...headerCellStyle, fontSize: "9px", p: 0.3 }}
                    >
                      {n}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {annexureTable2Data.length > 0 ? (
                  annexureTable2Data.map((row, index) => (
                    <TableRow key={row._id || index}>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.employeeName || getCandidateName()}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.conveyancePerquisite || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.domesticServices || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.passagesValue || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.otherBenefits || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.providentFundExcess || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.interestExcess || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, height: "32px" }}>
                        {row.totalCarried || ""}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}>
                      {getCandidateName()}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                    <TableCell sx={{ ...cellStyle, height: "32px" }}></TableCell>
                  </TableRow>
                )}
                {/* Add empty rows if needed */}
                {annexureTable2Data.length < 4 && (
                  [...Array(4 - annexureTable2Data.length)].map((_, rowIndex) => (
                    <TableRow key={`empty-annex2-${rowIndex}`}>
                      {Array.from({ length: 8 }).map((_, i) => (
                        <TableCell
                          key={i}
                          sx={{ ...cellStyle, height: "32px" }}
                        />
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3 }}>
            <Typography align="right" sx={{ fontSize: "11px", mb: 1 }}>
              Signature of the employee
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                width: "140px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: "auto",
                mb: 1.2,
              }}
            >
              {candidateData?.applicant_form_data?.signature && (
                <img
                  src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                  alt="candidate-signature"
                  style={{
                    maxHeight: "22px",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Form12B;